import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import Store from "./redux/store";
import { loadUser } from "./redux/reducers/userSlice";
import {
  getAllSubcategories,
  getCategories,
} from "./redux/reducers/categorySlice";
import { fetchProducts } from "./redux/reducers/productSlice";
import { fetchWishlist } from "./redux/reducers/wishlistSlice";
import { useOnlineStatus } from "./components/isOnline.js/IsOnline";
import { useDispatch, useSelector } from "react-redux";

// Keep these eager — small, and needed on every page anyway
import BottomTabBar from "./components/bottomTab/bottomTab";
import ProtectedRoute from "./components/routes/protectedRoute";
import ProtectedAdminRoute from "./components/routes/protectedAdminRoute";
import ProtectedManagerRoute from "./components/routes/ProtectedManagerRoute";
import GoogleAnalytics from "./analytics/GoogleAnalytics";
import OfflinePage from "./components/offline/offline";
import Shipping from "./components/info/shipping";
import FAQs from "./components/info/faqs";
import OurStory from "./components/info/ourstory";
import ContactUs from "./components/info/contactus";
import StoreLocator from "./components/info/storelocator";
import Returnspolicy from "./components/info/returns";
import Homepage from "./pages/Homepage";
// Public pages — lazy loaded
// const Homepage = lazy(() => import("./pages/Homepage"));
const Login = lazy(() => import(/* webpackPrefetch: true */ "./pages/Login"));
const NotFoundPage = lazy(() => import("./components/notFound/notFoundPage"));
const AllProducts = lazy(
  () => import(/* webpackPrefetch: true */ "./pages/AllProducts"),
);
const CookieConsent = lazy(() => import("./cookie-consent/CookieConsent"));
const ProductDetails = lazy(
  () => import(/* webpackPrefetch: true */ "./pages/ProductDetails"),
);
const Collections = lazy(() => import("./pages/Collections"));
const OAuthResult = lazy(() => import("./pages/Oauth"));
const Privacy = lazy(() => import("./pages/policies"));
const Terms = lazy(() => import("./pages/terms"));
const Cart = lazy(() => import(/* webpackPrefetch: true */ "./pages/Cart"));
const Pos = lazy(() => import("./pages/Pos"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const CheckOut = lazy(
  () => import(/* webpackPrefetch: true */ "./pages/CheckOut"),
);
const ManagerCheckOut = lazy(() => import("./pages/InstoreCheckout"));
const OrderSuccessPage = lazy(() => import("./pages/OrderSuccessPage"));
const TikTokCallback = lazy(() => import("./pages/tiktokOauth"));

// Dashboard has one default export + several named exports in a single file.
// All these lazy() calls point at the same module specifier, so webpack still
// bundles it as ONE chunk — it just won't load until a dashboard route is hit.
const loadDashboardModule = () => import("./components/dashboard/dashboard");
const Dashboard = lazy(loadDashboardModule);
const Account = lazy(() =>
  loadDashboardModule().then((m) => ({ default: m.Account })),
);
const Addresses = lazy(() =>
  loadDashboardModule().then((m) => ({ default: m.Addresses })),
);
const DeleteAccount = lazy(() =>
  loadDashboardModule().then((m) => ({ default: m.DeleteAccount })),
);
const Help = lazy(() =>
  loadDashboardModule().then((m) => ({ default: m.Help })),
);
const Logout = lazy(() =>
  loadDashboardModule().then((m) => ({ default: m.Logout })),
);
const Notifications = lazy(() =>
  loadDashboardModule().then((m) => ({ default: m.Notifications })),
);
const OrderDetails = lazy(() =>
  loadDashboardModule().then((m) => ({ default: m.OrderDetails })),
);
const Orders = lazy(() =>
  loadDashboardModule().then((m) => ({ default: m.Orders })),
);
const Payment = lazy(() =>
  loadDashboardModule().then((m) => ({ default: m.Payment })),
);
const Returns = lazy(() =>
  loadDashboardModule().then((m) => ({ default: m.Returns })),
);
const Wishlist = lazy(() =>
  loadDashboardModule().then((m) => ({ default: m.Wishlist })),
);

// Admin pages — lazy loaded (biggest win: regular customers never download this code)
const AdminDashboardPage = lazy(() => import("./pages/admin/adminDashboard"));
const SalesReportPage = lazy(() => import("./pages/admin/AdminSalesReport"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminViewOrder = lazy(() => import("./pages/admin/AdminViewOrder"));
const AdminLocation = lazy(() => import("./pages/admin/AdminLocation"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminQRCodes = lazy(() => import("./pages/admin/adminQrCodes"));
// NOTE: "AdminCustomers" and "AdminTransactions" were both imported from the same
// file in the original code, but only AdminTransactions was ever rendered. Only
// one lazy import is kept — importing the unused one too would pull this chunk
// back into the main bundle and defeat the split.
const AdminTransactions = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminInventory = lazy(() => import("./pages/admin/AdminInventory"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminReturns = lazy(() => import("./pages/admin/AdminReturns"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
// Same situation here: "AdminMarketing" and "AdminCollections" both pointed at this
// file; only AdminCollections was rendered, so only it gets a lazy import.
const AdminCollections = lazy(() => import("./pages/admin/AdminMarketing"));
const AdminUsersRoles = lazy(() => import("./pages/admin/AdminUserRoles"));
const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

function PageLoader() {
  return <div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>;
}

function App() {
  const isOnline = useOnlineStatus();
  const dispatch = useDispatch();
  const { isAuthenticated, loaded, loading } = useSelector(
    (state) => state.user,
  );

  // Boot: load user ONCE
  useEffect(() => {
    if (!loaded && !loading) {
      dispatch(loadUser());
    }
  }, [dispatch, loaded, loading]);

  // Non-auth data: fire immediately
  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(getCategories());
    dispatch(getAllSubcategories());
  }, [dispatch]);

  // Auth-dependent data: ONLY after we know user is logged in
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchWishlist());
    }
  }, [dispatch, isAuthenticated]);

  if (!isOnline) {
    return <OfflinePage />;
  }

  return (
    <div>
      <BrowserRouter>
        <GoogleAnalytics />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Homepage />} />

            <Route path="/login" element={<Login />} />

            <Route path="/auth/tiktok/callback" element={<TikTokCallback />} />
            <Route path="*" element={<NotFoundPage />} />
            <Route path="/all-products" element={<AllProducts />} />
            <Route path="/product/:slug" element={<ProductDetails />} />
            <Route path="/collections/:slug" element={<Collections />} />
            <Route path="/oauth/result" element={<OAuthResult />} />
            <Route path="/policy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/shipping" element={<Shipping />} />
            <Route path="/return-policy" element={<Returnspolicy />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/our-story" element={<OurStory />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/store-locator" element={<StoreLocator />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pos"
              element={
                <ProtectedManagerRoute>
                  <Pos />
                </ProtectedManagerRoute>
              }
            />
            <Route
              path="/order/success"
              element={
                <ProtectedRoute>
                  <OrderSuccessPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/order/:orderNo"
              element={
                <ProtectedRoute>
                  <OrderDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/addresses"
              element={
                <ProtectedRoute>
                  <Addresses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/info"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route
              path="/delete-account"
              element={
                <ProtectedRoute>
                  <DeleteAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <ProtectedRoute>
                  <Returns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wishlist"
              element={
                <ProtectedRoute>
                  <Wishlist />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment"
              element={
                <ProtectedRoute>
                  <Payment />
                </ProtectedRoute>
              }
            />

            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              }
            />
            <Route
              path="/logout"
              element={
                <ProtectedRoute>
                  <Logout />
                </ProtectedRoute>
              }
            />

            <Route path="/cart" element={<Cart />} />
            <Route
              path="/trackorder"
              element={
                <ProtectedRoute>
                  <TrackOrder />
                </ProtectedRoute>
              }
            />

            <Route
              path="/checkout"
              element={
                <ProtectedRoute>
                  <CheckOut />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instore-checkout"
              element={
                <ProtectedManagerRoute>
                  <ManagerCheckOut />
                </ProtectedManagerRoute>
              }
            />
            {/* admin routes */}
            <Route
              path="/admin-dashboard"
              element={
                <ProtectedAdminRoute>
                  <AdminDashboardPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/sales"
              element={
                <ProtectedAdminRoute>
                  <SalesReportPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <ProtectedAdminRoute>
                  <AdminOrders />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/order/:orderNo"
              element={
                <ProtectedAdminRoute>
                  <AdminViewOrder />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/locations"
              element={
                <ProtectedAdminRoute>
                  <AdminLocation />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <ProtectedAdminRoute>
                  <AdminProducts />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/codes"
              element={
                <ProtectedAdminRoute>
                  <AdminQRCodes />
                </ProtectedAdminRoute>
              }
            />

            <Route
              path="/admin/customers"
              element={
                <ProtectedAdminRoute>
                  <AdminTransactions />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/inventory"
              element={
                <ProtectedAdminRoute>
                  <AdminInventory />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <ProtectedAdminRoute>
                  <AdminCategories />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/coupons"
              element={
                <ProtectedAdminRoute>
                  <AdminCoupons />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedAdminRoute>
                  <AdminReturns />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/reviews"
              element={
                <ProtectedAdminRoute>
                  <AdminReviews />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/marketing"
              element={
                <ProtectedAdminRoute>
                  <AdminCollections />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedAdminRoute>
                  <AdminUsersRoles />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/invoices"
              element={
                <ProtectedAdminRoute>
                  <AdminInvoices />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedAdminRoute>
                  <AdminSettings />
                </ProtectedAdminRoute>
              }
            />
          </Routes>
        </Suspense>
        <Toaster position="top-center" reverseOrder={false} />
        <BottomTabBar />
      </BrowserRouter>
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
    </div>
  );
}

export default App;
